import { Trash } from '@phosphor-icons/react';
import { authMessage } from '@shh/shared';
import { Button, PasswordField, Sheet, Text } from '@shh/ui';
import { useState } from 'react';
import { deleteAccount, friendlyError } from '../data/api';
import { useFamily } from '../data/family';
import { useSession } from '../data/session';
import { accountText as t } from '../i18n/account';
import { firebase } from '../lib/firebase';
import { locale } from '../lib/locale';
import { startText } from '../i18n/start';

/** What happens to the family – decides the wording (Mike, 11.10.). */
type Family =
  { lastParent: true; familyName: string } | { lastParent: false; personName?: string } | undefined;

/**
 * "Konto löschen" (1.17): a quiet button right next to "Abmelden" – no settings section of its
 * own (Mike, 11.10.) – and the window that says what goes and asks for the password once more.
 */
export function DeleteAccountButton({ family }: { family?: Family }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const user = firebase().auth.currentUser;
  const withPassword = Boolean(user?.providerData.some((p) => p.providerId === 'password'));

  const remove = async () => {
    setBusy(true);
    setError(undefined);
    try {
      // on success the session ends and the login page says goodbye
      await deleteAccount(withPassword ? password : undefined);
    } catch (e) {
      const code = (e as { code?: unknown }).code;
      if (typeof code === 'string' && code.startsWith('auth/')) {
        setError(
          /invalid-credential|wrong-password|invalid-login/.test(code)
            ? startText.wrongPassword
            : authMessage(code, locale),
        );
      } else {
        setError(friendlyError(e));
      }
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="quiet" icon={Trash} className="self-start" onPress={() => setOpen(true)}>
        {t.entry}
      </Button>
      <Sheet
        isOpen={open}
        onOpenChange={(o) => {
          if (busy) return;
          setOpen(o);
          setError(undefined);
          setPassword('');
        }}
        title={t.title}
      >
        <form
          className="flex flex-col gap-gap-md"
          onSubmit={(e) => {
            e.preventDefault();
            void remove();
          }}
        >
          <Text>{t.whatGoes}</Text>
          {family?.lastParent ? (
            <>
              <Text weight="semibold">{t.familyGoes(family.familyName)}</Text>
              <Text size="sm" tone="secondary">
                {t.exportTip}
              </Text>
            </>
          ) : (
            family?.personName && <Text>{t.personStays(family.personName)}</Text>
          )}
          {withPassword ? (
            <PasswordField
              label={t.password}
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              isRequired
            />
          ) : (
            <Text size="sm" tone="secondary">
              {t.google}
            </Text>
          )}
          {error && (
            <p role="alert" className="font-semibold text-feedback-danger">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-gap-sm">
            <Button
              type="submit"
              variant="danger"
              icon={Trash}
              isDisabled={busy || (withPassword && !password)}
            >
              {busy ? t.busy : t.confirm}
            </Button>
            <Button variant="quiet" isDisabled={busy} onPress={() => setOpen(false)}>
              {t.cancel}
            </Button>
          </div>
        </form>
      </Sheet>
    </>
  );
}

/** Inside a family: works out whether the family goes along or the person stays. */
export function FamilyDeleteAccountButton() {
  const f = useFamily();
  const session = useSession();
  if (session.status !== 'ready' || session.role === 'device') return null;
  const me = f.members.find((m) => m.id === session.memberId || m.uid === session.user.uid);
  const otherParent = f.members.some(
    (m) => m.role === 'parent' && m.uid !== undefined && m.uid !== session.user.uid,
  );
  const family: Family =
    session.role === 'parent' && !otherParent
      ? { lastParent: true, familyName: f.household?.name ?? startText.yourFamily }
      : { lastParent: false, personName: me?.name };
  return <DeleteAccountButton family={family} />;
}
